import { useCallback, useEffect, useRef, useState } from "react";

/* Pick a run of days by dragging across them — with a mouse straight away,
   on a touch screen after pressing and holding a day (a plain swipe must
   still scroll the page, so a finger has to say it means to select first).

   Cells opt in with the props from bind(group, key): `group` keeps a drag
   inside one row (a month grid is one group; each room type's rate row in
   the list view is its own), `key` is the day as YYYY-MM-DD, so the range
   is just the smallest and largest key touched.

   onPick(group, firstKey, lastKey) fires when a drag ends. A plain click or
   tap is left alone for the cell's own onClick, which is how a single day
   still opens; a drag swallows the click that follows it — at the window,
   since by then the panel it opened may be what's under the finger, and a
   click on its backdrop would close it again. */
const HOLD_MS = 380;
const MOVE_CANCEL_PX = 10;

function keyAt(x, y, group) {
  const el = document.elementFromPoint(x, y)?.closest?.("[data-range-key]");
  return el && el.getAttribute("data-range-group") === group ? el.getAttribute("data-range-key") : null;
}

export default function useRangeSelect(onPick) {
  const [sel, setSel] = useState(null);          // { group, a, b } while dragging
  const g = useRef(null);                        // the gesture in progress
  const pick = useRef(onPick);
  const lastTouch = useRef(0);
  pick.current = onPick;

  const finish = useCallback(() => {
    const s = g.current;
    g.current = null;
    if (!s) return;
    clearTimeout(s.timer);
    if (!s.active) return;
    setSel(null);
    const lo = s.a < s.b ? s.a : s.b, hi = s.a < s.b ? s.b : s.a;
    // A mouse click on one day is the cell's own onClick; a press-and-hold on
    // a touch screen has no click after it, so it opens the day from here.
    if (lo !== hi || s.touch) {
      const swallow = e => { e.stopPropagation(); e.preventDefault(); window.removeEventListener("click", swallow, true); };
      window.addEventListener("click", swallow, true);
      setTimeout(() => window.removeEventListener("click", swallow, true), 400);
      pick.current(s.group, lo, hi);
    }
  }, []);

  useEffect(() => {
    const move = (x, y) => {
      const s = g.current;
      if (!s?.active) return;
      const k = keyAt(x, y, s.group);
      if (k && k !== s.b) { s.b = k; setSel({ group: s.group, a: s.a, b: k }); }
    };
    const onMouseMove = e => move(e.clientX, e.clientY);
    const onTouchMove = e => {
      const s = g.current;
      if (!s) return;
      const t = e.touches[0];
      if (!s.active) {
        // Moved before the hold completed: it's a scroll, not a selection.
        if (Math.hypot(t.clientX - s.x, t.clientY - s.y) > MOVE_CANCEL_PX) { clearTimeout(s.timer); g.current = null; }
        return;
      }
      if (e.cancelable) e.preventDefault();       // hold the page still while selecting
      move(t.clientX, t.clientY);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", finish);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", finish);
    window.addEventListener("touchcancel", finish);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", finish);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", finish);
      window.removeEventListener("touchcancel", finish);
    };
  }, [finish]);

  const bind = (group, key) => ({
    "data-range-group": group,
    "data-range-key": key,
    onMouseDown: e => {
      // The mouse events a browser fakes after a touch are not a new drag.
      if (e.button !== 0 || Date.now() - lastTouch.current < 800) return;
      g.current = { group, a: key, b: key, active: true, touch: false };
      setSel({ group, a: key, b: key });
    },
    onTouchStart: e => {
      lastTouch.current = Date.now();
      if (e.touches.length !== 1) { g.current = null; return; }
      const t = e.touches[0];
      const s = { group, a: key, b: key, active: false, touch: true, x: t.clientX, y: t.clientY };
      s.timer = setTimeout(() => {
        if (g.current !== s) return;
        s.active = true;
        setSel({ group, a: key, b: key });
        try { navigator.vibrate?.(10); } catch {}
      }, HOLD_MS);
      g.current = s;
    },
    // A long press would otherwise open the browser's own menu.
    onContextMenu: e => { if (g.current?.touch) e.preventDefault(); },
  });

  const isSelected = (group, key) => {
    if (!sel || sel.group !== group) return false;
    const lo = sel.a < sel.b ? sel.a : sel.b, hi = sel.a < sel.b ? sel.b : sel.a;
    return key >= lo && key <= hi;
  };

  return { bind, isSelected, selecting: !!sel };
}
